import { Fragment, useMemo } from 'react';
import { AccountingUserTypeEnum, TransactionCategoryEnum, TransactionStatusEnum } from '@/enums';
import { ControlledSelect } from '@/components/controlled';
import { useFormContext, useWatch } from 'react-hook-form';
import { LabelRequired } from '@/components/hoc';
import { useRequestGetAccountingUsers } from '@/hooks/httpRequest';
import type { TFormData } from '..';

const FormCreateOrEditTransactionBodyInputEmployeeOrInvestor = () => {
	const { control } = useFormContext<TFormData>();
	const category = useWatch({ control, name: 'category' });
	const readonly = useWatch({ control, name: 'readonly' });
	const isSalary = category === TransactionCategoryEnum.SALARY;
	const isInvestor = category === TransactionCategoryEnum.INVESTOR;

	const employeeUsers = useRequestGetAccountingUsers(
		{
			type: [AccountingUserTypeEnum.EMPLOYEE],
			status: ['ACTIVE'],
			limit: 100
		},
		isSalary
	);

	const investorUsers = useRequestGetAccountingUsers(
		{
			type: [AccountingUserTypeEnum.INVESTOR],
			status: ['ACTIVE'],
			limit: 100
		},
		isInvestor
	);

	const employeeOptions = useMemo(
		() => employeeUsers.accountingUsers.map(u => ({ title: u.name, value: u._id })),
		[employeeUsers.accountingUsers]
	);

	const investorOptions = useMemo(
		() => investorUsers.accountingUsers.map(u => ({ title: u.name, value: u._id })),
		[investorUsers.accountingUsers]
	);

	if (!isSalary && !isInvestor) {
		return <Fragment />;
	}

	return (
		<div className={isInvestor ? 'grid grid-cols-2 gap-3 md:gap-4' : 'w-full'}>
			<div className="flex flex-col gap-1.5 text-left">
				<LabelRequired className="text-[11px] md:text-xs">{isSalary ? 'Employé' : 'Investisseur'}</LabelRequired>
				<ControlledSelect
					control={control}
					rules={{ required: true }}
					name={isSalary ? 'data.employeeId' : 'data.investorId'}
					disabled={readonly}
					placeholder={isSalary ? 'Sélectionner un employé' : 'Sélectionner un investisseur'}
					options={isSalary ? employeeOptions : investorOptions}
					className="text-left"
				/>
			</div>
			{isInvestor && (
				<div className="flex flex-col gap-1.5 text-left">
					<LabelRequired className="text-[11px] md:text-xs">Statut</LabelRequired>
					<ControlledSelect
						control={control}
						rules={{ required: true }}
						name="data.status"
						disabled={readonly}
						placeholder="Statut"
						options={[
							{ title: 'En attente', value: TransactionStatusEnum.PENDING },
							{ title: 'Payé', value: TransactionStatusEnum.PAID }
						]}
					/>
				</div>
			)}
		</div>
	);
};

export default FormCreateOrEditTransactionBodyInputEmployeeOrInvestor;
